import React, { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import moment, { Moment } from 'moment';
import { format } from 'date-fns';
import { cs, sk, es, enGB, hu, uk, ru, de, fr, pl } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './BirthDatePicker.module.scss';
import { useAppContext } from '../../../../../../Services/AppContext';
import useTranslation from '../../../../../../Hooks/useTranslation';
import { toMomentOrNull } from '../DatePicker/DatePicker';

interface Props {
	value?: Moment | string | null;
	onChange?: (date: string | null) => void;
	disabled?: boolean;
	minDate?: Moment;
	maxDate?: Moment;
}

const BIRTH_DATE_FORMAT = 'YYYY-MM-DD';

type PickerStep = 'year' | 'month' | 'day';

const ll: Record<string, any> = {
	cs,
	sk,
	hu,
	en: enGB,
	ru,
	uk,
	es,
	de,
	fr,
	pl,
};

for (const l in ll) {
	registerLocale(l, ll[l]);
}

const BirthDatePicker: React.FC<Props> = ({
	value = null,
	onChange,
	disabled,
	minDate,
	maxDate,
}) => {
	const { language } = useAppContext();
	const { t } = useTranslation();
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const normalizedValue = useMemo(
		() => toMomentOrNull(value, language)?.startOf('day') ?? null,
		[value, language],
	);

	const [selectedDate, setSelectedDate] = useState<Date | null>(
		normalizedValue ? normalizedValue.toDate() : null,
	);

	const [draftDate, setDraftDate] = useState<Date | null>(null);

	const [step, setStep] = useState<PickerStep>('year');
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		setSelectedDate(normalizedValue ? normalizedValue.toDate() : null);
		setDraftDate(null);
	}, [normalizedValue]);

	useEffect(() => {
		if (language) moment.locale(language);
	}, [language]);

	const localeObj = useMemo(() => ll[language] ?? ll.cs, [language]);

	const handleChange = (date: Date | null) => {
		if (!date) return;

		if (step === 'year') {
			setDraftDate(date);
			setStep('month');
			return;
		}

		if (step === 'month') {
			setDraftDate(date);
			setStep('day');
			return;
		}

		setSelectedDate(date);
		onChange?.(moment(date).format(BIRTH_DATE_FORMAT));
		setIsOpen(false);
		setStep('year');
		setDraftDate(null);
	};

	const handleTitleClick = () => {
		setStep(prev => {
			if (prev === 'day') return 'month';
			if (prev === 'month') return 'year';
			return 'year';
		});
	};

	const setPortalWidth = () => {
		const w = wrapperRef.current?.offsetWidth;
		const portal = document.getElementById('react-datepicker-portal');

		if (w && portal) {
			portal.style.setProperty('--birthdp-width', `${w}px`);
		}
	};

	const openPicker = () => {
		setPortalWidth();
		setIsOpen(true);
		setStep('year');
		setDraftDate(selectedDate);
	};

	const getOpenToDate = () => {
		const base =
			draftDate ?? selectedDate ?? moment().subtract(25, 'years').toDate();

		if (minDate && moment(base).isBefore(minDate, 'day'))
			return minDate.toDate();

		if (maxDate && moment(base).isAfter(maxDate, 'day'))
			return maxDate.toDate();

		return base;
	};

	return (
		<div className='react-birth-date-picker' ref={wrapperRef}>
			<DatePicker
				selected={selectedDate}
				onChange={handleChange}
				open={isOpen}
				onInputClick={openPicker}
				onFocus={openPicker}
				onClickOutside={() => {
					setIsOpen(false);
					setStep('year');
					setDraftDate(null);
				}}
				locale={language}
				dateFormat='dd.MM.yyyy'
				showYearPicker={step === 'year'}
				showMonthYearPicker={step === 'month'}
				disabled={disabled}
				minDate={minDate ? minDate.toDate() : undefined}
				maxDate={maxDate ? maxDate.toDate() : undefined}
				openToDate={getOpenToDate()}
				placeholderText={t('base.pickBirthDate')}
				className={styles.input}
				wrapperClassName='react-birth-date-picker'
				portalId='react-datepicker-portal'
				popperClassName='birthdp-popper'
				calendarClassName='birthdp-calendar'
				renderCustomHeader={({
					date,
					decreaseYear,
					increaseYear,
					decreaseMonth,
					increaseMonth,
				}) => {
					const prev = step === 'year' ? decreaseYear : decreaseMonth;
					const next = step === 'year' ? increaseYear : increaseMonth;

					const title =
						step === 'year'
							? format(date, 'yyyy', { locale: localeObj })
							: format(date, 'LLLL yyyy', { locale: localeObj });

					return (
						<div className='react-datepicker__custom-header'>
							<div
								className='react-datepicker__custom-header__title'
								onClick={handleTitleClick}
							>
								{title}
							</div>
							<div
								className='react-datepicker__custom-header__prev'
								onClick={prev}
							/>
							<div
								className='react-datepicker__custom-header__next'
								onClick={next}
							/>
						</div>
					);
				}}
			/>
		</div>
	);
};

export default BirthDatePicker;
